import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Plus, Eye } from 'lucide-react'
import { StatsGrid } from '../../components/features/dashboard/StatsGrid'
import { ActivityList } from '../../components/features/dashboard/ActivityList'
import { PerformanceMetrics } from '../../components/features/dashboard/PerformanceMetrics'
import { TopBooksList } from '../../components/features/dashboard/TopBooksList'
import { QuickActions } from '../../components/features/dashboard/QuickActions'
import { STYLES } from '../../lib/constants/dashboard-styles'
import type { DashboardData, Activity, TopBook, DashboardStats, DashboardApiResponse } from '../../types/dashboard'

export const Route = createFileRoute('/admin/')({
  component: Dashboard,
})

function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch('/api/dashboard/stats', {
          credentials: 'include',
        })

        if (!response.ok) {
          if (response.status === 401) {
            window.location.href = '/admin/login'
            return
          }
          throw new Error('Failed to fetch dashboard data')
        }

        const result: DashboardApiResponse = await response.json()

        if (result.success) {
          // Transform API response to DashboardData format
          const { products, recentProducts, recentPosts } = result.data

          const stats: DashboardStats = {
            totalBooks: Number(products.total) || 0,
            publishedBooks: Number(products.published) || 0,
            draftBooks: Number(products.total) - Number(products.published) || 0,
            totalUsers: 0, // Not available in current API
            newBooksThisMonth: Number(products.featured) || 0, // Using featured as approximation
          }

          // Transform recent products and posts into activities
          const activities: Activity[] = [
            ...recentProducts.map((product) => ({
              id: `product-${product.id}`,
              type: 'book' as const,
              title: `Buku "${product.title}" ${product.status === 'published' ? 'dipublikasikan' : 'dibuat'}`,
              user: 'Admin',
              time: new Date(product.created_at).toLocaleDateString('id-ID'),
            })),
            ...recentPosts.map((post) => ({
              id: `post-${post.id}`,
              type: 'system' as const,
              title: `Artikel "${post.title}" ${post.status === 'published' ? 'dipublikasikan' : 'dibuat'}`,
              user: 'Admin',
              time: new Date(post.created_at).toLocaleDateString('id-ID'),
            })),
          ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 5)

          // Create top books from recent published products
          const topBooks: TopBook[] = recentProducts
            .filter(p => p.status === 'published')
            .slice(0, 5)
            .map(p => ({
              title: p.title,
              author: 'Unknown',
              views: Math.floor(Math.random() * 1000), // Placeholder since API doesn't provide views
            }))

          setData({
            stats,
            recentActivity: activities,
            topBooks,
          })
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // Provide default values to handle undefined
  const stats = data?.stats ?? {
    totalBooks: 0,
    totalUsers: 0,
    newBooksThisMonth: 0,
    publishedBooks: 0,
    draftBooks: 0,
  }
  const recentActivity = data?.recentActivity ?? []
  const topBooks = data?.topBooks ?? []

  if (loading) {
    return (
      <div className={STYLES.container}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-yellow-500"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={STYLES.container}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p>Error loading dashboard: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 text-sm underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={STYLES.container}>
      {/* Page Header */}
      <div className={STYLES.pageHeader}>
        <div>
          <h1 className={STYLES.pageTitle}>Dashboard</h1>
          <p className={STYLES.pageSubtitle}>
            Selamat datang kembali! Berikut ringkasan toko Anda hari ini.
          </p>
        </div>

        <div className="flex gap-3">
          <Button asChild className={STYLES.buttonPrimary}>
            <a href="/admin/products/create" className="flex items-center gap-2">
              <Plus className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="leading-none">Tambah Buku</span>
            </a>
          </Button>

          <Button variant="outline" asChild className={STYLES.buttonOutline}>
            <a href="/shop" target="_blank" className="flex items-center gap-2">
              <Eye className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="leading-none">Lihat Toko</span>
            </a>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsGrid stats={stats} />

      {/* Activity & Metrics */}
      <div className={STYLES.grid3}>
        <ActivityList activities={recentActivity}/>
        <PerformanceMetrics stats={stats} />
      </div>

      {/* Top Books */}
      <TopBooksList books={topBooks} />

      {/* Quick Actions */}
      <QuickActions />
    </div>
  )
}
